import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import NotesButton from "../components/HouseholdNotes";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { Check, X, Plus, TrendingUp, AlertTriangle, Trash2, CalendarClock, Info, Pencil } from "lucide-react";
import { useStore } from "../context/StoreContext";
import SpendingCalendar from "../components/SpendingCalendar";
import { billList, forecast, monthlyEquivalent } from "../lib/finance";
import { fmt, fmt0, dateLabel, uid, today } from "../lib/format";
import { PageHeader, Section, Kpi, NativeSelect, CategorySelect, Banner, Money, Empty, ChartTip } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { BillReminderBanner } from "../components/BillReminders";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

const FREQ = ["weekly", "biweekly", "semi-monthly", "monthly", "quarterly", "annual"];

// Removing a detected bill also dismisses its suggestion so it doesn't reappear
export function deleteBill(update, bill) {
  if (!window.confirm(`Delete ${bill.name}? It will be removed from your calendar, reminders and forecast.`)) return false;
  update("Delete bill", (d) => {
    d.bills = d.bills.filter((x) => x.id !== bill.id);
    d.billMoves = (d.billMoves || []).filter((m) => m.itemKey !== bill.id);
    if (bill.key && !d.dismissedBills.includes(bill.key)) d.dismissedBills.push(bill.key);
  }, [{ action: "bill", summary: `Deleted bill ${bill.name}` }]);
  toast("Bill deleted — use Undo to restore");
  return true;
}

const parseDays = (s) => {
  const d = String(s || "").split(/[^0-9]+/).map(Number).filter((x) => x >= 1 && x <= 31).sort((a, b) => a - b);
  return d.length === 2 && d[0] !== d[1] ? d : null;
};

// bill = saved bill (edit), suggestion (confirm with edits) or null (new)
function BillDialog({ bill, onClose }) {
  const { state, update } = useStore();
  const isSaved = !!bill?.id;
  const [f, setF] = useState(bill ? { name: bill.name, amount: Math.abs(bill.amount), frequency: bill.frequency, nextDate: bill.nextDate || today(), category: bill.category, isIncome: !!bill.isIncome, active: bill.active !== false, days: (bill.days || []).join(", ") } : { name: "", amount: "", frequency: "monthly", nextDate: today(), category: "subscriptions", isIncome: false, active: true, days: "" });
  const save = () => {
    if (!f.name.trim() || !(Math.abs(Number(f.amount)) > 0)) return toast.error("Name and amount required");
    if (!f.nextDate) return toast.error("Choose the next due date");
    const days = f.frequency === "semi-monthly" ? parseDays(f.days) : null;
    if (f.frequency === "semi-monthly" && !days) return toast.error("Enter the two days of the month, e.g. 1, 15");
    const data = { name: f.name.trim(), amount: Math.abs(Number(f.amount)), frequency: f.frequency, nextDate: f.nextDate, category: f.category, isIncome: f.isIncome, active: f.active, days };
    if (isSaved) {
      update(`Edit bill ${data.name}`, (d) => {
        const b = d.bills.find((x) => x.id === bill.id);
        const scheduleChanged = bill.nextDate !== data.nextDate || b.frequency !== data.frequency || String(b.days) !== String(data.days);
        Object.assign(b, data);
        if (scheduleChanged) {
          b.extraDates = [];
          d.billMoves = (d.billMoves || []).filter((m) => m.itemKey !== b.id);
        }
      }, [{ action: "bill", summary: `Edited bill ${data.name}` }]);
      toast.success("Bill updated");
    } else {
      update(bill ? "Confirm bill" : "Add bill", (d) => d.bills.push({ id: uid(), key: bill?.key || null, ...data, confirmed: true, source: bill ? "detected" : "manual" }), [{ action: "bill", summary: `${bill ? "Confirmed" : "Added"} bill ${data.name}` }]);
      toast.success(bill ? "Bill confirmed" : "Bill added");
    }
    onClose();
  };
  const remove = () => {
    if (deleteBill(update, bill)) onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bill-dialog">
        <DialogHeader><DialogTitle>{isSaved ? `Edit ${bill.name}` : bill ? "Review and confirm" : "Add a recurring bill or income"}</DialogTitle><DialogDescription>Used for your calendar, reminders and forecast.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="bill-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="bill-amount-input" /></div>
            <div><Label>How often</Label><NativeSelect value={f.frequency} onChange={(v) => setF({ ...f, frequency: v })} className="w-full" testid="bill-frequency-select">{FREQ.map((x) => <option key={x}>{x}</option>)}</NativeSelect></div>
            <div><Label>Next due</Label><Input type="date" value={f.nextDate} onChange={(e) => setF({ ...f, nextDate: e.target.value })} data-testid="bill-date-input" /></div>
            <div><Label>Category</Label><CategorySelect categories={state.categories} value={f.category} onChange={(v) => setF({ ...f, category: v })} className="w-full" testid="bill-category-select" /></div>
            {f.frequency === "semi-monthly" && <div className="col-span-2"><Label>Days of the month</Label><Input placeholder="e.g. 1, 15" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} data-testid="bill-days-input" /></div>}
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.isIncome} onCheckedChange={(v) => setF({ ...f, isIncome: v })} data-testid="bill-income-switch" /> This is income (e.g. paycheck)</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="bill-active-switch" /> Active (include in calendar, reminders and forecast)</label>
          {isSaved && f.nextDate !== bill.nextDate && <p className="text-xs text-muted-foreground">Changing the next due date resets any one-off moves for this bill.</p>}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {isSaved ? <Button variant="ghost" className="text-expense" onClick={remove} data-testid="bill-delete-button"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button> : <span />}
          <Button onClick={save} data-testid="bill-save-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BillsTab() {
  const { state, update } = useStore();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const { saved, suggestions } = useMemo(() => billList(state), [state]);
  const confirm = (s) => update("Confirm bill", (d) => d.bills.push({ id: uid(), key: s.key, name: s.name, amount: s.amount, frequency: s.frequency, days: s.days || null, nextDate: s.nextDate, category: s.category, isIncome: s.isIncome, confirmed: true, active: true, source: "detected" }));
  const outflows = saved.filter((b) => !b.isIncome && b.active !== false);
  const monthlyTotal = outflows.reduce((s, b) => s + monthlyEquivalent(b.amount, b.frequency), 0);
  const subs = [...saved, ...suggestions].filter((b) => b.category === "subscriptions" && !b.isIncome);
  const increases = [...saved, ...suggestions].filter((b) => b.increase);
  const upcoming = saved.filter((b) => b.active !== false && b.nextDate && b.nextDate <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10)).sort((a, b) => a.nextDate.localeCompare(b.nextDate));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 stagger">
        <Kpi label="Confirmed bills" value={outflows.length} testid="bills-count" />
        <Kpi label="Bills per month" value={fmt0(monthlyTotal)} tone="expense" testid="bills-monthly-total" />
        <Kpi label="Due in 30 days" value={upcoming.length} testid="bills-upcoming-count" />
        <Kpi label="Suggestions to review" value={suggestions.length} tone={suggestions.length ? "warning" : undefined} testid="bills-suggestions-count" />
      </div>
      {increases.length > 0 && (
        <Banner tone="warning" icon={TrendingUp} testid="bill-increases-banner">
          <b>Price increases detected:</b> {increases.map((b) => `${b.name} (${fmt(b.increase.from)} → ${fmt(b.increase.to)})`).join(", ")}
        </Banner>
      )}
      {subs.length >= 2 && (
        <Banner icon={Info} testid="subscriptions-review-banner">
          You have <b>{subs.length} subscriptions</b> totaling about {fmt(subs.reduce((s, b) => s + monthlyEquivalent(b.amount, b.frequency), 0))}/month. We can't tell whether you use them — mark the ones you still use, and consider cancelling the rest.
        </Banner>
      )}

      {suggestions.length > 0 && (
        <Section title="We found these repeating payments" subtitle="Confirm the ones that are real recurring bills. Low-confidence items appeared only once." testid="bill-suggestions">
          <ul className="divide-y">
            {suggestions.map((s) => (
              <li key={s.key} className="py-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`suggestion-${s.key}`}>
                <div className="min-w-0">
                  <div className="font-medium">{s.name} {s.isIncome && <Badge className="bg-income-soft text-income border-0 ml-1">Income</Badge>} <Badge variant="outline" className="ml-1 capitalize">{s.confidence} confidence</Badge></div>
                  <p className="text-xs text-muted-foreground">{s.categoryName} · {s.frequency}{s.confidence === "low" ? " (assumed)" : ""} · seen {s.count}× · last {dateLabel(s.lastDate)} · next ≈ {dateLabel(s.nextDate)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Money value={s.amount} className="font-semibold" />
                  <Button size="sm" onClick={() => confirm(s)} data-testid={`confirm-bill-${s.key}`}><Check className="h-4 w-4 mr-1" /> Confirm</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(s)} title="Edit before confirming" data-testid={`edit-suggestion-${s.key}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => update("Dismiss bill", (d) => d.dismissedBills.push(s.key))} data-testid={`dismiss-bill-${s.key}`}><X className="h-4 w-4" /></Button>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Your recurring bills & income" action={<Button size="sm" onClick={() => setAdding(true)} data-testid="add-bill-button"><Plus className="h-4 w-4 mr-1" /> Add bill</Button>} testid="saved-bills">
        {saved.length ? (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-xs uppercase text-muted-foreground border-b"><tr><th className="p-2 text-left">Merchant</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Frequency</th><th className="p-2 text-left">Next expected</th><th className="p-2 text-left">Notes</th><th className="p-2"></th></tr></thead>
              <tbody>
                {saved.map((b) => (
                  <tr key={b.id} className="border-b last:border-0" data-testid={`bill-row-${b.id}`}>
                    <td className="p-2 font-medium"><button className="hover:text-primary text-left" onClick={() => setEditing({ ...state.bills.find((x) => x.id === b.id), nextDate: b.nextDate })} data-testid={`bill-name-${b.id}`}>{b.name}</button> {b.isIncome && <Badge className="bg-income-soft text-income border-0 ml-1">Income</Badge>}<div className="-ml-2 mt-0.5"><NotesButton kind="bills" id={b.id} /></div></td>
                    <td className="p-2 text-right"><Money value={b.amount} className={b.isIncome ? "text-income" : ""} /></td>
                    <td className="p-2 capitalize">{b.frequency}</td>
                    <td className="p-2">{dateLabel(b.nextDate)}</td>
                    <td className="p-2 text-xs">
                      {b.usualDateChange && <span className="block text-muted-foreground" data-testid={`usual-date-changed-${b.id}`}>Usual date moved {dateLabel(b.usualDateChange.from)} → {dateLabel(b.usualDateChange.to)}</span>}
                      {b.increase && <span className="text-warning flex items-center gap-1"><TrendingUp className="h-3 w-3" /> up {b.increase.pct}%</span>}
                      {b.category === "subscriptions" && <label className="flex items-center gap-1.5 mt-1"><Switch checked={!!b.inUse} onCheckedChange={(v) => update("Mark subscription", (d) => (d.bills.find((x) => x.id === b.id).inUse = v))} data-testid={`in-use-${b.id}`} /> I use this</label>}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Switch checked={b.active !== false} onCheckedChange={(v) => update("Toggle bill", (d) => (d.bills.find((x) => x.id === b.id).active = v))} title="Active" data-testid={`active-${b.id}`} />
                      <Button variant="ghost" size="icon" onClick={() => setEditing({ ...state.bills.find((x) => x.id === b.id), nextDate: b.nextDate })} title="Edit" data-testid={`edit-bill-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteBill(update, b)} data-testid={`delete-bill-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty icon={CalendarClock} title="No confirmed bills yet" text="Confirm the suggestions above or add bills manually." />}
      </Section>
      {adding && <BillDialog onClose={() => setAdding(false)} />}
      {editing && <BillDialog key={editing.id || editing.key} bill={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ForecastTab() {
  const { state, update } = useStore();
  const [days, setDays] = useState(90);
  const fc = useMemo(() => forecast(state, 90), [state]);
  const w = fc.windows.find((x) => x.days === days);
  const data = fc.series.slice(0, days + 1).map((p) => ({ date: dateLabel(p.date).replace(/, \d{4}/, ""), Balance: p.balance }));
  const events = fc.events.filter((e) => e.date <= fc.series[days].date);
  return (
    <div className="space-y-6">
      {!fc.balanceKnown && (
        <Banner tone="warning" icon={AlertTriangle} testid="forecast-balance-missing">
          We don't know your current account balance yet. Enter it so the forecast starts from the right number.
          <span className="flex gap-2 mt-2">
            {state.accounts.filter((a) => a.type !== "credit").slice(0, 1).map((a) => (
              <Input key={a.id} type="number" placeholder={`${a.name} balance`} className="h-9 w-48 bg-background" onBlur={(e) => e.target.value !== "" && update("Set balance", (d) => { const x = d.accounts.find((y) => y.id === a.id); x.balance = Number(e.target.value); x.balanceDate = today(); })} data-testid="forecast-balance-input" />
            ))}
          </span>
        </Banner>
      )}
      <div className="flex gap-2">
        {[30, 60, 90].map((d) => <Button key={d} variant={days === d ? "default" : "outline"} onClick={() => setDays(d)} data-testid={`forecast-${d}-button`}>{d} days</Button>)}
      </div>
      {fc.shortage && fc.shortage.date <= fc.series[days].date && <Banner tone="danger" icon={AlertTriangle} testid="forecast-shortage-warning"><b>Warning:</b> projected expenses may exceed available funds. Your balance is estimated to drop below {fmt(fc.threshold)} around <b>{dateLabel(fc.shortage.date)}</b> ({fmt(fc.shortage.balance)}).</Banner>}
      {w && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 stagger">
          <Kpi label="Starting balance" value={fmt0(fc.startBalance)} testid="forecast-start" hint="Checking + savings + cash" />
          <Kpi label="Expected income" value={fmt0(w.income)} tone="income" testid="forecast-income" hint={`${fmt0(w.confirmedIncome)} confirmed`} />
          <Kpi label="Upcoming bills" value={fmt0(w.bills)} tone="expense" testid="forecast-bills" />
          <Kpi label="Everyday spending" value={fmt0(w.variable)} tone="expense" testid="forecast-variable" hint="Estimate from history" />
          <Kpi label={`Balance in ${days} days`} value={fmt0(w.endBalance)} tone={w.endBalance < 0 ? "expense" : undefined} testid="forecast-end" hint={`Lowest: ${fmt0(w.minBalance)}`} />
        </div>
      )}
      <Section title="Projected balance" subtitle="Solid figures come from your confirmed bills; detected patterns and average spending are predictions." testid="forecast-chart">
        <div className="h-72">
          <ResponsiveContainer>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={Math.ceil(days / 8)} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip content={<ChartTip />} />
              <ReferenceLine y={fc.threshold} stroke="hsl(var(--expense))" strokeDasharray="4 4" label={{ value: "Low balance", fontSize: 11, fill: "hsl(var(--expense))" }} />
              <Area type="monotone" dataKey="Balance" stroke="#0D9488" fill="#0D9488" fillOpacity={0.15} strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Section>
      <Section title="Expected transactions" testid="forecast-events">
        {events.length ? (
          <ul className="divide-y text-sm">
            {events.map((e, i) => (
              <li key={i} className="py-2 flex justify-between gap-3">
                <span><span className="text-muted-foreground w-28 inline-block">{dateLabel(e.date)}</span> {e.name}</span>
                <span className="flex items-center gap-2">{e.confirmed ? <Badge variant="outline">Confirmed bill</Badge> : <Badge className="bg-warning-soft text-warning border-0">Predicted</Badge>}<Money value={e.amount} signed /></span>
              </li>
            ))}
          </ul>
        ) : <Empty title="No expected bills or income" text="Confirm recurring bills on the Bills tab to improve the forecast." />}
        <p className="text-xs text-muted-foreground mt-3">Everyday (non-recurring) spending is spread evenly at about {fmt(fc.variableMonthly)}/month, based on your history. Forecasts are estimates, not guarantees.</p>
      </Section>
    </div>
  );
}

export default function Bills() {
  const [params] = useSearchParams();
  return (
    <div data-testid="bills-page">
      <PageHeader title="Bills & forecast" subtitle="Recurring payments we detected, a calendar of bills and paydays, and a 30/60/90-day look ahead at your cash." />
      <div className="mb-4"><BillReminderBanner limit={8} /></div>
      <Tabs defaultValue={["forecast", "calendar"].includes(params.get("tab")) ? params.get("tab") : "bills"}>
        <TabsList className="mb-5">
          <TabsTrigger value="bills" data-testid="tab-bills">Recurring bills</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast">Cash flow forecast</TabsTrigger>
        </TabsList>
        <TabsContent value="bills"><BillsTab /></TabsContent>
        <TabsContent value="calendar"><SpendingCalendar /></TabsContent>
        <TabsContent value="forecast"><ForecastTab /></TabsContent>
      </Tabs>
    </div>
  );
}
